import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Warehouse, Save, Trash2, Plus, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
}

function getFYOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear + 1; y >= currentYear - 8; y--) {
    options.push(`FY ${y - 1}-${String(y).slice(-2)}`);
  }
  return options;
}

export default function ClosingStock({ companyContext }) {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-date snapshot edit state
  const [editAsOfDate, setEditAsOfDate] = useState(null);  // YYYY-MM-DD or "__new__"
  const [editFY, setEditFY] = useState(getFYOptions()[1]);
  const [entries, setEntries] = useState([]);
  // For "Add new snapshot" pickers above the list
  const todayYMD = () => new Date().toISOString().slice(0, 10);
  const [newDate, setNewDate] = useState(todayYMD());
  const [newFY, setNewFY] = useState(getFYOptions()[1]);

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  const fetchStocks = useCallback(async () => {
    try {
      const params = { company_id: companyId };
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(`${API}/closing-stock`, { params });
      setStocks(res.data);
    } catch {
      toast.error("Failed to load closing stock data");
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId]);

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  const startEditByDate = async (asOfDate, fyHint) => {
    try {
      const params = { company_id: companyId };
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(
        `${API}/closing-stock/by-date/${encodeURIComponent(asOfDate)}`,
        { params },
      );
      setEditAsOfDate(asOfDate);
      setEditFY(res.data.fy_label || fyHint || newFY);
      setEntries(res.data.entries?.length > 0 ? res.data.entries : [
        { item_name: "", quantity: 0, rate: 0, amount: 0 },
      ]);
    } catch {
      setEditAsOfDate(asOfDate);
      setEditFY(fyHint || newFY);
      setEntries([{ item_name: "", quantity: 0, rate: 0, amount: 0 }]);
    }
  };

  const startNewSnapshot = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      toast.error("Pick a valid date (YYYY-MM-DD) before adding a snapshot");
      return;
    }
    startEditByDate(newDate, newFY);
  };

  const addRow = () => {
    setEntries([...entries, { item_name: "", quantity: 0, rate: 0, amount: 0 }]);
  };

  const removeRow = (idx) => {
    setEntries(entries.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    const updated = [...entries];
    if (field === "item_name") {
      updated[idx] = { ...updated[idx], [field]: value };
    } else {
      const numVal = parseFloat(value) || 0;
      updated[idx] = { ...updated[idx], [field]: numVal };
      if (field === "quantity" || field === "rate") {
        const qty = updated[idx].quantity || 0;
        const rate = updated[idx].rate || 0;
        if (qty > 0 && rate > 0) {
          updated[idx].amount = qty * rate;
        }
      }
    }
    setEntries(updated);
  };

  const handleSave = async () => {
    if (!editAsOfDate) return;
    const validEntries = entries.filter((e) => e.item_name.trim());
    try {
      await axios.post(`${API}/closing-stock`, {
        fy_label: editFY,
        as_of_date: editAsOfDate,
        date: editAsOfDate,
        entries: validEntries,
        company_id: companyId,
        branch_id: branchId,
      });
      toast.success(`Closing stock snapshot saved for ${editAsOfDate}`);
      setEditAsOfDate(null);
      fetchStocks();
    } catch {
      toast.error("Failed to save closing stock");
    }
  };

  const handleDeleteByDate = async (asOfDate, fyFallback) => {
    try {
      const params = { company_id: companyId };
      if (branchId) params.branch_id = branchId;
      if (asOfDate) {
        await axios.delete(
          `${API}/closing-stock/by-date/${encodeURIComponent(asOfDate)}`,
          { params },
        );
      } else if (fyFallback) {
        // Legacy: delete the undated FY snapshot
        await axios.delete(
          `${API}/closing-stock/${encodeURIComponent(fyFallback)}`,
          { params },
        );
      }
      toast.success("Snapshot deleted");
      fetchStocks();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const total = entries.reduce((s, e) => s + (e.amount || 0), 0);

  // ---- EDIT MODE ----
  if (editAsOfDate) {
    return (
      <div data-testid="closing-stock-edit">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setEditAsOfDate(null)} data-testid="cs-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Closing Stock — As of {editAsOfDate}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                FY {editFY} · Add stock items and their valuation
              </p>
            </div>
          </div>
          <Button size="sm" onClick={handleSave} data-testid="cs-save-btn">
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>

        {/* Snapshot meta */}
        <div className="mb-4 bg-white border border-zinc-200 rounded-lg p-4 flex items-center gap-6 flex-wrap"
             data-testid="cs-date-section">
          <div className="flex items-center gap-2">
            <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500">As of</label>
            <input
              type="date"
              value={editAsOfDate}
              onChange={(e) => setEditAsOfDate(e.target.value)}
              className="px-3 py-1.5 border border-zinc-300 rounded-md text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
              data-testid="cs-date-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500">FY</label>
            <select
              value={editFY}
              onChange={(e) => setEditFY(e.target.value)}
              className="px-3 py-1.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
              data-testid="cs-fy-edit-select"
            >
              {getFYOptions().map((fy) => (
                <option key={fy} value={fy}>{fy}</option>
              ))}
            </select>
          </div>
        </div>

        <Card className="border border-slate-200 shadow-none mb-4">
          <CardContent className="p-0">
            <Table data-testid="cs-edit-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Item Name</TableHead>
                  <TableHead className="text-right font-semibold w-28">Quantity</TableHead>
                  <TableHead className="text-right font-semibold w-32">Rate</TableHead>
                  <TableHead className="text-right font-semibold w-36">Amount</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow key={entry.item_name || `entry-${idx}`}>
                    <TableCell>
                      <input
                        type="text"
                        value={entry.item_name}
                        onChange={(e) => updateRow(idx, "item_name", e.target.value)}
                        placeholder="Stock item name"
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid={`cs-name-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.quantity || ""}
                        onChange={(e) => updateRow(idx, "quantity", e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid={`cs-qty-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.rate || ""}
                        onChange={(e) => updateRow(idx, "rate", e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid={`cs-rate-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <input
                        type="number"
                        step="0.01"
                        value={entry.amount || ""}
                        onChange={(e) => updateRow(idx, "amount", e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid={`cs-amount-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRow(idx)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        data-testid={`cs-remove-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="cs-add-row-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Total Closing Stock</p>
            <p className="text-xl font-bold text-slate-900 font-mono tabular-nums" data-testid="cs-total">
              &#8377; {formatCurrency(total)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- LIST MODE ----
  // De-duplicate by (as_of_date, fy_label) so an updated snapshot doesn't show twice
  return (
    <div data-testid="closing-stock">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="cs-title">Closing Stock</h1>
        <p className="text-sm text-slate-500 mt-1">
          Record stock value at any number of dates. Reports pick the latest snapshot ≤ the period end.
        </p>
      </div>

      <Card className="border border-slate-200 shadow-none mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Warehouse className="w-4 h-4 text-slate-400" />
            <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500">As of</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono"
              data-testid="cs-new-date"
            />
            <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500">FY</label>
            <select
              value={newFY}
              onChange={(e) => setNewFY(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
              data-testid="cs-fy-select"
            >
              {getFYOptions().map((fy) => (
                <option key={fy} value={fy}>{fy}</option>
              ))}
            </select>
            <Button size="sm" onClick={startNewSnapshot} data-testid="cs-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add snapshot
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner"></div></div>
      ) : (
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Warehouse className="w-4 h-4" /> Saved Snapshots
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="cs-list-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-36">As of</TableHead>
                  <TableHead className="font-semibold w-32">Financial Year</TableHead>
                  <TableHead className="text-center font-semibold w-24">Items</TableHead>
                  <TableHead className="text-right font-semibold w-40">Total Value</TableHead>
                  <TableHead className="text-center font-semibold w-32">Last Updated</TableHead>
                  <TableHead className="text-right font-semibold w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stocks.map((s, i) => {
                  const asOf = s.as_of_date || s.date || null;
                  const rowKey = `${asOf || "legacy"}-${s.fy_label || ""}-${i}`;
                  return (
                    <TableRow key={rowKey} data-testid={`cs-row-${asOf || "legacy"}`}>
                      <TableCell className="font-mono text-sm text-zinc-700">
                        {asOf || <span className="text-zinc-400 text-xs">Not dated (legacy)</span>}
                      </TableCell>
                      <TableCell className="font-medium">{s.fy_label}</TableCell>
                      <TableCell className="text-center font-mono text-sm">{s.entries?.length || 0}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">
                        &#8377; {formatCurrency(s.total)}
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-500">
                        {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => startEditByDate(asOf || todayYMD(), s.fy_label)}
                            className="text-xs h-7"
                            data-testid={`cs-edit-${asOf || "legacy"}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => handleDeleteByDate(asOf, s.fy_label)}
                            className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cs-del-${asOf || "legacy"}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {stocks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      No snapshots yet. Pick a date above and click <strong>Add snapshot</strong>.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
